import { useState, useEffect, useRef, useLayoutEffect, useCallback, ReactNode } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import icons
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Globe, House } from "lucide-react";
// import images
import logoName from "../../image/logo_name.svg";
import logoNameLight from "../../image/logo_name_light.svg";
import blklogoName from "../../image/logo_drugomic_nav.svg";
import healthLogoName from "../../image/logo_health_nav.svg";
import educationLogoName from "../../image/logo_education_nav.svg";

// import data
import navUnitsData from "../../data/navUnits.json";
import { DataStructure } from '../../types';
// import components
import { NavToggleButton, AllGraphen, LanguageSelect, OtherTabs, MobileDropdownUnit } from "./NavDisplays";
// import context
import { useNavTab, useLang, useScreenSize, useMobileNavOpen } from "../Context";
// import global var
import { maxWidthNav, typedVocab } from "../GlobalVar";
import { useLocation } from "react-router-dom";
import { useHealthNavOffsetSync } from "../health/healthNavOffset";
import { scrollLandingPageToTop } from "../health/healthScroll";
import {
    corporateHomeHref,
    isAgentsLandingPath,
    isLightSubsidiaryPath,
    isSubsidiaryBusinessUnitPath,
    normalizeNavPathname,
} from "./navPathUtils";
import { HealthLang } from "../health/healthTypes";

const typedNavUnits : DataStructure = navUnitsData;

const navDropdownEase = [0.25, 0.46, 0.45, 0.94] as const;

type NavDropdownMotionProps = {
    initial: false | { opacity: number };
    animate: { opacity: number; transition?: { duration: number; ease: readonly [number, number, number, number] } };
    exit: { opacity: number; transition?: { duration: number; ease: readonly [number, number, number, number] } };
};

function navDropdownMotion(reduceMotion: boolean | null): NavDropdownMotionProps {
    if (reduceMotion) {
        return {
            initial: false,
            animate: { opacity: 1 },
            exit: { opacity: 1 },
        };
    }
    return {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.16, ease: navDropdownEase } },
        exit: { opacity: 0, transition: { duration: 0.1, ease: navDropdownEase } },
    };
}

function healthNavHomePath(lang: string): string {
    if (lang === "Zh") return "/tw/health";
    if (lang === "Jp") return "/jp/health";
    return "/health";
}

function isHealthLandingPath(pathname: string): boolean {
    return (
        pathname === "/health" ||
        pathname === "/tw/health" ||
        pathname === "/jp/health"
    );
}

function educationNavHomePath(lang: string): string {
    if (lang === "Zh") return "/tw/education";
    if (lang === "Jp") return "/jp/education";
    return "/education";
}

function isEducationLandingPath(pathname: string): boolean {
    return (
        pathname === "/education" ||
        pathname === "/tw/education" ||
        pathname === "/jp/education"
    );
}

function isCorporateHomeLandingPath(pathname: string): boolean {
    return normalizeNavPathname(pathname) === "/";
}

export default function Nav() {
    const { screenSize } = useScreenSize();
    return (
        <>
            { screenSize === "Large" ? <NavBar /> : <MobileNavBar /> }
        </>
    )
};

export function NavBar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const { lang } = useLang();
    const { navTab, setNavTab } = useNavTab();
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const navShellRef = useRef<HTMLDivElement>(null);
    const navBarRef = useRef<HTMLDivElement>(null);

    useHealthNavOffsetSync(navBarRef, [pathname]);

    useEffect(() => {
        if (!navTab.Open) {
            return;
        }

        function handlePointerDown(event: MouseEvent | TouchEvent) {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            if (navShellRef.current?.contains(target)) {
                return;
            }
            setNavTab({ Name: "", Open: false, LeftPos: 0 });
        }

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [navTab.Open, setNavTab]);

    useEffect(() => {
        function handleScroll() {
            const scrollTop = document.documentElement.scrollTop;
            setIsScrolled(scrollTop > 0); 
        };
        // Listen for scroll events
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);


    let style = navTab["Open"]
                ? "h-fit px-8 py-3 bg-black/95 border-b-1 border-grey-300 lg:px-16 min-[1280px]:px-32"
                : "h-fit px-8 py-3 bg-black/95 lg:px-16 min-[1280px]:px-32";

    return (
        <div ref={navShellRef} className="fixed top-0 w-full z-50 ">
            <motion.div
                ref={navBarRef}
                data-site-nav
                className={style}
                style={{
                    boxShadow:
                      (isScrolled && isLightSubsidiaryPath(pathname))
                        ? "0px 1px 5px 0px rgba(0,0,0,0.1)"
                        : undefined
                  }}
                animate={{ backgroundColor:
                    isScrolled || navTab["Open"]
                      ? isLightSubsidiaryPath(pathname)
                      ? "white"
                      : "rgba(0, 0, 0, 0.95)"
                      : isLightSubsidiaryPath(pathname)
                      ? "rgba(255, 255, 255, 0.5)"
                      : "transparent"}}
                transition={{ duration: 0.3 }}
            >
                <div className="m-auto flex min-w-0 items-center gap-3" style={{ maxWidth: `${maxWidthNav}px` }}>
                        <Link
                        to={
                            pathname.includes("/drugomics")
                                ? "#"
                                : pathname.includes("/health")
                                  ? healthNavHomePath(lang)
                                  : pathname.includes("/education")
                                    ? educationNavHomePath(lang)
                                  : lang === "Zh"
                                    ? "/tw"
                                    : lang === "Jp"
                                    ? "/jp"
                                    : "/"
                        }
                        onClick={(e) => {
                            const scrollTopWithHashClear = () => {
                                scrollLandingPageToTop((to, options) =>
                                    navigate(to, options)
                                );
                            };

                            if (pathname.includes("/drugomics")) {
                                e.preventDefault();
                                scrollTopWithHashClear();
                                return;
                            }
                            if (
                                pathname.includes("/health") &&
                                isHealthLandingPath(pathname)
                            ) {
                                e.preventDefault();
                                scrollTopWithHashClear();
                                return;
                            }
                            if (
                                pathname.includes("/education") &&
                                isEducationLandingPath(pathname)
                            ) {
                                e.preventDefault();
                                scrollTopWithHashClear();
                                return;
                            }
                            if (isCorporateHomeLandingPath(pathname)) {
                                e.preventDefault();
                                scrollTopWithHashClear();
                            }
                        }}
                        >
                        <img
                            className="mr-5 cursor-pointer"
                            src={
                                pathname.includes("/drugomics")
                                  ? blklogoName
                                  : pathname.includes("/health")
                                  ? healthLogoName
                                  : pathname.includes("/education")
                                  ? educationLogoName
                                  : isAgentsLandingPath(pathname)
                                  ? logoNameLight
                                  : logoName
                              }
                            alt="logo-name"
                        />
                        </Link>
                    <div className="mr-auto flex min-w-0 items-center justify-center">
                        <LeftNav />
                    </div>
                    <div className="ml-auto flex shrink-0 items-center justify-center">
                        <RightNav />
                    </div>
                </div>
                
            </motion.div>
            {navTab["Open"] ? <NavDropdown key="desktop-nav-dropdown" /> : null}
        </div>
    )
};

const navDropdownShellDuration = 0.22;

function navDropdownPanelContent(
    tabName: string,
    anchorOffsetLeft: number
): ReactNode {
    if (tabName === "Business Units") {
        return <AllGraphen />;
    }
    if (tabName === "Language") {
        return <LanguageSelect />;
    }
    return <OtherTabs tabId={tabName} anchorOffsetLeft={anchorOffsetLeft} />;
}

const navDropdownPanelBodyClass =
    "px-8 pt-5 pb-5 lg:px-16 min-[1280px]:px-32";

function navDropdownAnchorOffset(
    anchorEl: HTMLDivElement | null,
    leftPos: number
): number {
    if (!anchorEl || !leftPos) {
        return 0;
    }
    return Math.max(0, leftPos - anchorEl.getBoundingClientRect().left);
}

export function NavDropdown() {
    const panelContentRef = useRef<HTMLDivElement>(null);
    const ghostMeasureRef = useRef<HTMLDivElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const { navTab } = useNavTab();
    const reduceMotion = useReducedMotion();
    const dropdownMotion = navDropdownMotion(reduceMotion);
    const tabName = navTab["Name"];
    const anchorOffset = navDropdownAnchorOffset(anchorRef.current, navTab.LeftPos);

    const [shellHeight, setShellHeight] = useState<number | null>(null);

    const measureFrom = useCallback((source: HTMLDivElement | null) => {
        if (!source) {
            return 0;
        }
        return source.scrollHeight;
    }, []);

    const applyShellHeight = useCallback(() => {
        const activeHeight = measureFrom(panelContentRef.current);
        const ghostHeight = measureFrom(ghostMeasureRef.current);
        const nextHeight = activeHeight > 0 ? activeHeight : ghostHeight;
        if (nextHeight > 0) {
            setShellHeight(nextHeight);
        }
    }, [measureFrom]);

    useLayoutEffect(() => {
        applyShellHeight();
        const frameId = requestAnimationFrame(() => {
            applyShellHeight();
        });
        return () => cancelAnimationFrame(frameId);
    }, [tabName, anchorOffset, applyShellHeight]);

    useEffect(() => {
        const el = panelContentRef.current;
        if (!el) {
            return;
        }
        const observer = new ResizeObserver(() => {
            applyShellHeight();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [applyShellHeight, tabName]);

    const shellTransition = reduceMotion
        ? { duration: 0 }
        : { duration: navDropdownShellDuration, ease: navDropdownEase };

    const contentFadeIn = reduceMotion
        ? { initial: false as const, animate: { opacity: 1 } }
        : {
              initial: { opacity: 0 },
              animate: { opacity: 1, transition: { duration: 0.16, ease: navDropdownEase } },
          };

    return (
        <motion.div
            className="overflow-hidden bg-black"
            initial={dropdownMotion.initial}
            animate={{
                height: shellHeight ?? "auto",
                opacity: dropdownMotion.animate.opacity,
            }}
            transition={{
                height: shellTransition,
                opacity: {
                    duration: 0.16,
                    ease: navDropdownEase,
                },
            }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 -z-10 opacity-0"
            >
                <div ref={ghostMeasureRef} className={navDropdownPanelBodyClass}>
                    <div
                        className="m-auto"
                        style={{ maxWidth: `${maxWidthNav}px` }}
                    >
                        {navDropdownPanelContent(tabName, anchorOffset)}
                    </div>
                </div>
            </div>

            <div ref={panelContentRef} className={navDropdownPanelBodyClass}>
                <div
                    ref={anchorRef}
                    className="m-auto"
                    style={{ maxWidth: `${maxWidthNav}px` }}
                >
                    <motion.div
                        key={tabName}
                        initial={contentFadeIn.initial}
                        animate={contentFadeIn.animate}
                    >
                        {navDropdownPanelContent(tabName, anchorOffset)}
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
};



export function LeftNav() {
    const { lang } = useLang();
    const location = useLocation();
    const isLightNav = isLightSubsidiaryPath(location.pathname);
    const textColorClass = isLightNav ? "text-black" : "text-white";

    return (
        <div className="w-fit h-fit grid grid-flow-col auto-cols-auto gap-5">
            <NavToggleButton id="Business Units" children={ <p className={textColorClass}>{typedVocab["Business Units"][lang]}</p> } />
            <NavToggleButton id="About" children={ <p className={textColorClass}>{typedVocab["About"][lang]}</p> } />
            <NavToggleButton id="Updates" children={ <p className={textColorClass}>{typedVocab["Updates"][lang]}</p> } />
        </div>
    )
};

export function RightNav() {
    const { lang } = useLang();
    const location = useLocation();
    const hLang = lang as HealthLang;
    const isLightNav = isLightSubsidiaryPath(location.pathname);
    const textColorClass = isLightNav ? "text-black" : "text-white";
    const showCorporateHomeLink = isSubsidiaryBusinessUnitPath(location.pathname);

    /** Match NavToggleButton closed state — pb-1 + border reserve underline space. */
    const rightNavItemShell =
        "flex items-center gap-2 cursor-pointer select-none pb-1 border-b-1 border-transparent";

    let languageTab = <>
                        <Globe size={16} className={`${textColorClass} shrink-0`}/>
                        <p className={`${textColorClass} hidden min-[1280px]:block`}>{typedNavUnits["Language"][lang]}</p>
                    </>

    return (
        <div className="w-fit h-fit shrink-0 grid grid-flow-col auto-cols-auto items-center gap-3 min-[1280px]:gap-4">
            {showCorporateHomeLink ? (
                <>
                    <Link
                        to={corporateHomeHref(hLang)}
                        className={`${textColorClass} ${rightNavItemShell} hidden min-[1280px]:flex whitespace-nowrap font-semibold`}
                    >
                        Graphen
                    </Link>
                    <Link
                        to={corporateHomeHref(hLang)}
                        className={`${textColorClass} ${rightNavItemShell} min-[1280px]:hidden`}
                        aria-label="Graphen Home"
                    >
                        <House size={16} className="shrink-0" aria-hidden="true" />
                    </Link>
                </>
            ) : null}
            <Link
                to={ lang === "Zh" ? "/tw/contact-us" : lang === "Jp" ? "/jp/contact-us" : "/contact-us"}
                className={`${textColorClass} ${rightNavItemShell} whitespace-nowrap`}
            >
                {typedVocab["Contact"][lang]}
            </Link>
            <Link
                to={ lang === "Zh" ? "/tw/careers" : lang === "Jp" ? "/jp/careers" : "/careers"}
                className={`${textColorClass} ${rightNavItemShell} whitespace-nowrap`}
            >
                {typedVocab["Careers"][lang]}
            </Link>
            <NavToggleButton
                id="Language"
                ariaLabel={typedVocab["Select Language"][lang]}
                children={languageTab}
            />
        </div>
    )
};


/////////////////
// MOBILE NAV //
////////////////
export function MobileNavBar() {
    const { mobileNavOpen, setMobileNavOpen } = useMobileNavOpen();
    const { lang } = useLang();
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const headerRef = useRef<HTMLDivElement>(null);
    const navShellRef = useRef<HTMLDivElement>(null);
    const reduceMotion = useReducedMotion();
    const dropdownMotion = navDropdownMotion(reduceMotion);

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isLangOpen, setIsLangOpen] = useState<boolean>(false);
    const [headerHeight, setHeaderheight] = useState<number>(0);

    useHealthNavOffsetSync(headerRef, []);

    useEffect(() => {
        setIsMenuOpen(false);
        setIsLangOpen(false);
    }, [pathname, lang, location.hash]);

    useEffect(() => {
        if (!isMenuOpen && !isLangOpen) {
            return;
        }

        function handlePointerDown(event: MouseEvent | TouchEvent) {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            if (navShellRef.current?.contains(target)) {
                return;
            }
            setIsMenuOpen(false);
            setIsLangOpen(false);
            setMobileNavOpen(false);
        }

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [isMenuOpen, isLangOpen, setMobileNavOpen]);

    useEffect(() => {
        if (!mobileNavOpen) {
            setIsMenuOpen(false);
            setIsLangOpen(false);
        };
    }, [mobileNavOpen]);

    useEffect(() => {
        if (isMenuOpen || isLangOpen) {
            setMobileNavOpen(true);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        };
    }, [isMenuOpen, isLangOpen]);

    useEffect(() => {
        handleResize();
        function handleResize() {
            if (headerRef.current) {
                const element = headerRef.current;
                const height = element.scrollHeight;
                setHeaderheight(height);
            };
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    let languageStyle = isLangOpen
                        ? "gap-1 grid grid-flow-col auto-cols-min flex items-center justify-center cursor-pointer pb-1 border-b-2 border-grey-300"
                        : "gap-1 grid grid-flow-col auto-cols-min flex items-center justify-center cursor-pointer pb-1 border-b-2 border-transparent"
    let languageTab = <div className={ languageStyle }>
                        <Globe size={16} />
                        <p className="text-white select-none text-nowrap">{typedNavUnits["Language"][lang]}</p>
                    </div>

    function openDropdown(type: string) {
        if (type === "menu") {
            setIsMenuOpen(!isMenuOpen);
            setIsLangOpen(false);
        } else if (type === "lang") {
            setIsLangOpen(!isLangOpen);
            setIsMenuOpen(false);
        };
    };

    return (
        <div ref={navShellRef} className="fixed top-0 w-screen z-50 overflow-hidden">
            <div ref={headerRef} data-site-nav className="w-full gap-auto grid grid-flow-col grid-cols-4 flex items-center justify-center bg-black/95 p-3">
                <div className="col-span-1 mr-auto cursor-pointer" onClick={() => openDropdown("menu")}>
                    <FontAwesomeIcon className="text-xl text-white" icon={ isMenuOpen ? faXmark : faBars } />
                </div>
                <Link
                    to={
                        lang === "Zh"
                            ? "/tw"
                            : lang === "Jp"
                              ? "/jp"
                              : "/"
                    }
                    className="col-span-2 m-auto"
                    onClick={(e) => {
                        setIsMenuOpen(false);
                        setIsLangOpen(false);
                        setMobileNavOpen(false);
                        if (isCorporateHomeLandingPath(pathname)) {
                            e.preventDefault();
                            scrollLandingPageToTop((to, options) =>
                                navigate(to, options)
                            );
                        }
                    }}
                >
                    <img
                        className="cursor-pointer"
                        src={logoName}
                        alt="logo-name"
                    />
                </Link>
                <div className="col-span-1 ml-auto text-white" onClick={() => openDropdown("lang")}>
                    { languageTab }
                </div>
            </div>
            <div className="relative w-full h-full overflow-y-auto" style={{ maxHeight: `calc(100vh - ${headerHeight}px)` }}>
                <AnimatePresence>
                    {isMenuOpen ? (
                        <motion.div
                            key="mobile-menu-dropdown"
                            initial={dropdownMotion.initial}
                            animate={dropdownMotion.animate}
                            exit={dropdownMotion.exit}
                        >
                            <MobileMenuDropdown />
                        </motion.div>
                    ) : null}
                    {isLangOpen ? (
                        <motion.div
                            key="mobile-lang-dropdown"
                            initial={dropdownMotion.initial}
                            animate={dropdownMotion.animate}
                            exit={dropdownMotion.exit}
                        >
                            <MobileLangDropdown />
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </div>
        </div>
    )
};

interface MobileNavDropdownFrameProps {
    children: ReactNode;
};
export function MobileNavDropdownFrame({ children } : MobileNavDropdownFrameProps) {
    return (
        <div className="h-full bg-black border-t-1 border-grey-300 select-none">
            { children }
        </div>
    )
};

export function MobileMenuDropdown() {
    const { lang } = useLang();
    return (
        <MobileNavDropdownFrame>
            <MobileDropdownUnit id="Business Units" label={typedVocab["Business Units"][lang]} toggle />
            <MobileDropdownUnit id="About" label={typedVocab["About"][lang]} toggle />
            <MobileDropdownUnit id="Updates" label={typedVocab["Updates"][lang]} toggle />
            <Link to={ lang === "Zh" ? "/tw/contact-us" : lang === "Jp" ? "/jp/contact-us" : "/contact-us" }>
                <MobileDropdownUnit id="Contact" label={typedVocab["Contact"][lang]} />
            </Link>
            <Link to={ lang === "Zh" ? "/tw/careers" : lang === "Jp" ? "/jp/careers" : "/careers" }>
                <MobileDropdownUnit id="Careers" label={typedVocab["Careers"][lang]} />
            </Link>
        </MobileNavDropdownFrame>
    )
};

export function MobileLangDropdown() {
    const { lang, setLang } = useLang();

    let showLang =  Object.keys(typedNavUnits["Language"]).map((i) => {
        let style = lang === i
                    ? "text-left cursor-pointer hover:text-teal-300 text-teal-300"
                    : "text-left cursor-pointer hover:text-teal-300 text-white"
        return (
            <p id={i} className={style} onClick={() => setLang(i)}>
                {typedNavUnits["Language"][i]}
            </p>
        )
    });

    return (
        <MobileNavDropdownFrame>
            <div className="w-full px-5 py-3">
                <div className="w-full py-3 border-b-1 border-grey-300">
                    <p className="text-md text-left text-white font-bold">{typedVocab["Select Language"][lang]}</p>
                </div>
                <div className="grid grid-flow-row auto-rows-auto gap-1 text-white py-2">
                    { showLang }
                </div>
            </div>
        </MobileNavDropdownFrame>
    )
}